import {
  DiscountType,
  IElement,
  ITax,
  LineItem,
  ProductListElementOptions,
  calculator,
  getTaxItemName,
} from '@gohighlevel/ghl-proposals-common'

import currency from 'currency.js'
import { computed } from 'vue'

const PRODUCT_LIST_SUM_TAXABLE_THEN_TAX_VERSION = 5

export const useLineItems = (element: IElement<ProductListElementOptions>) => {
  const selectedLineItems = computed(() => {
    if (element?.version > 1) {
      return element?.component?.options?.lineItems.filter(
        ({ selected }) => selected
      )
    }
    return element?.component?.options?.lineItems
  })

  const productListVersion = computed(() => element?.version)

  const subTotal = computed(() => {
    return Number(
      selectedLineItems.value
        ?.reduce((acc: number, { price, qty }: LineItem) => {
          acc += price * qty
          return acc
        }, 0)
        ?.toFixed(2)
    )
  })

  const totalDiscount = computed(() => {
    const discountValue = Number(
      element?.component?.options?.discountValue ?? 0
    )

    if (element?.component?.options?.discountType === DiscountType.PERCENTAGE) {
      const { percentageOf } = calculator(subTotal.value, element?.version)
      return Number(percentageOf(Number(discountValue.toFixed(2))))
    }

    return Number(discountValue.toFixed(2))
  })

  const amountDue = computed(() => {
    const value = Number(
      (
        subTotal.value -
        totalDiscount.value +
        appliedTax.value.reduce((acc, { tax, taxInclusive }) => {
          return taxInclusive ? acc : acc + tax
        }, 0)
      )?.toFixed(2)
    )
    return value
  })

  const paymentSchedules = computed(
    () => element?.component?.options?.paymentSchedules
  )

  const getDiscountAmountPerItem = (itemPrice: number) => {
    if (element?.component?.options?.discountType === DiscountType.PERCENTAGE) {
      const { percentageOf } = calculator(itemPrice, element?.version)
      return (
        itemPrice -
        percentageOf(Number(element?.component?.options?.discountValue))
      )
    } else {
      const totalDiscount = Number(element?.component?.options?.discountValue)
      const discountRatio = itemPrice / subTotal.value
      const itemDiscount = discountRatio * totalDiscount
      const discountedPrice = itemPrice - itemDiscount
      return discountedPrice
    }
  }

  // NOTE:
  // Product list versions > 5 version calculates tax using currency.js to avoid rounding errors
  const appliedTax = computed(() => {
    const version = element?.version || 0

    if (version >= PRODUCT_LIST_SUM_TAXABLE_THEN_TAX_VERSION) {
      return Object.values(
        selectedLineItems.value?.reduce((acc, lineItem) => {
          const isTaxInclusive = !!lineItem?.taxInclusive
          let itemPrice = lineItem.price

          if (isTaxInclusive) {
            const totalTaxRate =
              lineItem.taxes?.reduce(
                (acc, tax) => currency(acc).add(tax.rate).value,
                currency(0).value
              ) || currency(0).value
            const { percentageOf } = calculator(itemPrice, version)
            const includedTaxOnItem = percentageOf(
              (totalTaxRate / (100 + totalTaxRate)) * 100
            )
            itemPrice = currency(itemPrice).subtract(includedTaxOnItem).value
          }

          const itemSubTotal = getDiscountAmountPerItem(
            currency(itemPrice).multiply(lineItem.qty).value
          )
          lineItem.taxes?.forEach((tax: ITax) => {
            if (lineItem.price <= 0) return
            const taxKey = `${tax._id}_taxInclusive_${isTaxInclusive}`

            //Same calculation as in core package
            const itemTaxUnrounded = currency(itemSubTotal, { precision: 4 })
              .multiply(tax.rate)
              .divide(100).value
            const itemTax = currency(itemTaxUnrounded).value
            if (acc[taxKey]) {
              acc[taxKey] = {
                ...acc[taxKey],
                name: getTaxItemName(tax, isTaxInclusive),
                tax: currency(acc[taxKey]['tax']).add(itemTax).value,
                taxInclusive: isTaxInclusive,
              }
            } else {
              acc[taxKey] = {
                name: getTaxItemName(tax, isTaxInclusive),
                description: tax.description,
                _id: tax._id,
                rate: tax.rate,
                tax: itemTax,
                taxInclusive: isTaxInclusive,
              }
            }
          })
          return acc
        }, {} as Record<string, Pick<ITax, '_id' | 'description' | 'name' | 'rate'> & { tax: number; taxInclusive: boolean }>) ||
          []
      )
    }

    //Legacy behavior: per-line tax rounded then summed
    return Object.values(
      selectedLineItems.value?.reduce((acc, lineItem) => {
        const isTaxInclusive = !!lineItem?.taxInclusive
        let itemPrice = lineItem.price

        if (isTaxInclusive) {
          const totalTaxRate =
            lineItem.taxes?.reduce((acc, tax) => acc + tax.rate, 0) || 0
          const { percentageOf } = calculator(itemPrice, version)

          const includedTaxOnItem = percentageOf(
            (totalTaxRate / (100 + totalTaxRate)) * 100
          )

          itemPrice = itemPrice - includedTaxOnItem
        }

        const itemSubTotal = getDiscountAmountPerItem(itemPrice * lineItem.qty)
        const { percentageOf } = calculator(itemSubTotal, version)

        lineItem.taxes?.forEach((tax: ITax) => {
          const taxKey = `${tax._id}_taxInclusive_${isTaxInclusive}`
          if (acc[taxKey]) {
            acc[taxKey] = {
              ...acc[taxKey],
              name: getTaxItemName(tax, isTaxInclusive),
              tax: acc[taxKey]['tax'] + percentageOf(tax.rate),
              taxInclusive: isTaxInclusive,
            }
          } else {
            acc[taxKey] = {
              name: getTaxItemName(tax, isTaxInclusive),
              description: tax.description,
              _id: tax._id,
              rate: tax.rate,
              tax: percentageOf(tax.rate),
              taxInclusive: isTaxInclusive,
            }
          }
        })
        return acc
      }, {} as Record<string, Pick<ITax, '_id' | 'description' | 'name' | 'rate'> & { tax: number; taxInclusive: boolean }>) ||
        []
    )
  })

  return {
    subTotal,
    amountDue,
    totalDiscount,
    paymentSchedules,
    appliedTax,
    productListVersion,
  }
}
